import React, { useState } from 'react';
/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';

// ─── Images ───────────────────────────────────────────────────────────────────
import musicFolioImg from '../assets/Project1-Template/MusicPortfolio1.png';
import redditAppImg from '../assets/RedditApp/RedditAppNew.png';
import axiGuitarImg from '../assets/AxiProject/AxiNew.png';
import occDashboardImg from '../assets/OCC-Dashboard/OCC.png';
import hnklBartendingImg from '../assets/HNKL/1.png';
import archiveFilmStudioImg from '../assets/expiredfilmstudio/ArchiveFilmStudio.png';

import emailDev1Img from '../assets/Project-email-template/Email-dev1.png';
import emailDev2Img from '../assets/Project-email-template/Email-dev2.png';

import restaurantImg from '../assets/Project-Restaurant/Restaurant1.png';

// ─── Styles ───────────────────────────────────────────────────────────────────
const ProjectsSection = styled.section`
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.text};
  padding: 5.5rem 1.6rem 5rem;
  text-align: left;
`;

const Title = styled.h1`
  font-size: 2.6rem;
  margin-bottom: 1.75rem;
  letter-spacing: -0.03em;
`;

const ProjectsGrid = styled.div`
  display: grid;
  gap: 1.25rem;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  justify-content: center;
`;

const Card = styled.article`
  background: #f9f4ee;
  color: ${({ theme }) => theme.text};
  border-radius: 0px;
  padding: 2.8rem 2.05rem 2.05rem;
  border: 1px solid ${({ theme }) => theme.line || 'rgba(12,12,12,0.12)'};
  box-shadow: 0 6px 28px rgba(0,0,0,0.05);
  transition: transform 0.25s ease;

  &:hover { transform: translateY(-4px); }

  h3 {
    margin: 0.75rem 0 0.25rem;
    font-size: 1.25rem;
  }

  p {
    font-size: 1rem;
    margin: 0.5rem 0 1rem;
    color: ${({ theme }) => theme.textSecondary || '#666'};
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: contain; /* no cropping */
  display: block;
  margin: 0 auto; /* centers in container */
  border-radius: 0px;
  background: #fff; /* keep mockups neutral, avoid warm overlay */
  border: 1px solid ${({ theme }) => theme.line || 'rgba(12,12,12,0.12)'};
  padding: 18px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
`;

const Thumbs = styled.div`
  display: flex;
  justify-content: center;
  gap: .5rem;
  margin: .6rem 0 0.3rem;
  flex-wrap: wrap;
`;

const ThumbButton = styled.button`
  border: 2px solid ${({ active, theme }) => (active ? theme.navTextColor : 'transparent')};
  background: transparent;
  padding: 0;
  border-radius: 0px;
  cursor: pointer;
  line-height: 0;

  img {
    width: 64px;
    height: 44px;
    object-fit: cover;
    border-radius: 0;
    display: block;
  }

  &:hover {
    border-color: ${({ theme }) => theme.navTextColor};
  }
`;

const Meta = styled.div`
  display: flex;
  justify-content: center;
  gap: .5rem;
  flex-wrap: wrap;
  margin: 0 0 1rem;

  span {
    font-size: .8rem;
    padding: .25rem .6rem;
    border-radius: 0;
    background: #efece8;
    color: #5a554c;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.8rem;
  flex-wrap: wrap;

  a {
    text-decoration: none;
    padding: 0.6rem 1.2rem;
    border-radius: 0;
    font-weight: 600;
    font-size: 0.92rem;
    transition: all 0.2s ease;
    border: 1px solid ${({ theme }) => theme.line || 'rgba(12,12,12,0.12)'};
    color: ${({ theme }) => theme.text};
    background: transparent;

    &:first-of-type {
      background: ${({ theme }) => theme.accent || '#c61823'};
      border-color: ${({ theme }) => theme.accent || '#c61823'};
      color: ${({ theme }) => theme.background};
    }

    &:hover { transform: translateY(-1px); }
  }
`;

const FeatureNote = styled.p`
  font-style: italic;
  color: ${({ theme }) => theme.textSecondary || '#666'};
`;

const Caption = styled.p`
  font-size: 0.9rem;
  margin: 0.4rem 0 0.2rem;
  color: ${({ theme }) => theme.textSecondary || '#666'};
`;

// ─── Data ─────────────────────────────────────────────────────────────────────
const PROJECTS = [
  {
    id: 'hnkl-private-bartending',
    title: 'HNKL Private Bartending (2025)',
    images: [hnklBartendingImg],
    alt: 'HNKL Private Bartending website preview across devices',
    caption: 'Editorial hero layout, cocktail menu grid, responsive sections.',
    description: 'Luxury private bartending site with curated cocktail experiences in London. Editorial layout, responsive builds for desktop, tablet, and mobile.',
    tags: ['HTML', 'CSS', 'JavaScript', 'Responsive'],
    live: 'https://hnkl.netlify.app',
    github: 'https://github.com/lukaSt605n/hnkl',
  },
  {
    id: 'airline-dashboard',
    title: 'Airline Operations Dashboard (2024–2025)',
    images: [occDashboardImg],
    alt: 'Airline operations dashboard screenshot',
    caption: 'Rotating KPI datasets, responsive grid, Emotion theme system.',
    description: 'Qatar Airways–inspired control panel simulating airline operations. Uses rotating mock datasets for KPIs, charts, maps, and tables. Includes chart rendering, grid layout, API-style data rotation, and a structured component-based architecture. Modular React architecture with Emotion theming and responsive grid.',
    featureNote: 'Production-level dashboard UX built with real architecture — mock live data, modular design, fully responsive.',
    tags: ['React', 'Vite', 'Emotion', 'Recharts', 'Leaflet'],
    live: 'https://airlinedashboardproject.netlify.app/',
    github: 'https://github.com/lukaSt605n/airline-dashboard',
  },
  {
    id: 'archive-film-studio',
    title: 'Archive Film Studio (2026)',
    images: [archiveFilmStudioImg],
    alt: 'Archive Film Studio conceptual archive preview',
    caption: 'Three-frame volumes, typographic grid, minimal navigation.',
    description: 'Constraint-based conceptual analog photography archive built around intentional reduction. Each volume is limited to three selected frames, shifting focus to hierarchy and visual discipline. Emphasizes typography control, spatial rhythm, and a calm viewing flow. Demonstrates information architecture through volume structuring, constraint-driven design thinking, minimal UI and typography system, content reduction and hierarchy control, and clean React routing and layout structure.',
    tags: ['React', 'Vite', 'HTML', 'Responsive'],
    live: 'https://archivefilmstudio.com',
    github: 'https://github.com/lukaSt605n/-Users-lukassivak-Desktop-expired-archive',
  },
  {
    id: 'musicfolio',
    title: 'musicFolio Template (2024)',
    images: [musicFolioImg],
    alt: 'musicFolio template screenshot',
    caption: 'Template blocks, multimedia embeds, easy custom sections.',
    description: 'Responsive portfolio template for musicians and creatives. Built with clean layouts, easy customization, and modern multimedia support.',
    tags: ['HTML', 'CSS', 'JS', 'Responsive'],
    live: 'https://musicfolio.netlify.app',
    github: 'https://github.com/lukaSt605n/musicFolio',
    buy: 'https://bylukas.gumroad.com/l/xbiyf',
  },
  {
    id: 'reddit-client',
    title: 'Reddit Client App (2024)',
    images: [redditAppImg],
    alt: 'Reddit client app screenshot',
    caption: 'Redux state flow, subreddit search, API-driven lists.',
    description: 'React + Redux Reddit client with dark mode and subreddit search. Simple UI, API integration, and clean state structure.',
    tags: ['React', 'Redux', 'API', 'Dark Mode'],
    live: 'https://stellular-panda-33baa6.netlify.app',
    github: 'https://github.com/lukaSt605n/Reddit-client',
  },
  {
    id: 'axi-guitar',
    title: 'Axi Guitar Website (2026)',
    images: [axiGuitarImg],
    alt: 'Axi classical guitar tutor website screenshot',
    caption: 'Minimal typography, clean sections, accessibility focus.',
    description: 'Dark, minimal portfolio site for a classical guitar tutor. Built with HTML and CSS, focused on readability and a calm layout.',
    tags: ['HTML', 'CSS', 'Accessibility'],
    live: 'https://rococo-bavarois-a7e933.netlify.app',
    github: 'https://github.com/lukaSt605n/Website-AxiG',
  },
  {
    id: 'email-dev-1',
    title: 'Responsive Email Project #1 (2024)',
    images: [emailDev2Img],
    alt: 'Responsive HTML marketing email preview',
    caption: 'Bulletproof table layout, mobile-first testing, cross-client safe.',
    description: 'Bulletproof HTML email templates built mobile-first and tested across clients. Clean structure, safe markup, and consistent rendering.',
    tags: ['Email', 'HTML', 'CSS', 'Responsive'],
    live: 'https://luxury-longma-e7de24.netlify.app',
    github: 'https://github.com/lukaSt605n/email-dev2',
  },
  {
    id: 'email-dev-2',
    title: 'Responsive Email Project #2',
    images: [emailDev1Img],
    alt: 'Responsive HTML marketing email preview',
    caption: 'Bulletproof table layout, mobile-first testing, cross-client safe.',
    description: 'Bulletproof HTML email templates built mobile-first and tested across clients. Clean structure, safe markup, and consistent rendering.',
    tags: ['Email', 'HTML', 'CSS', 'Responsive'],
    live: 'https://email-dev-second.netlify.app',
    github: 'https://github.com/lukaSt605n/email-dev-second',
  },
  {
    id: 'restaurant-site',
    title: 'Restaurant Website Template (2024)',
    images: [restaurantImg],
    alt: 'Restaurant website template preview',
    caption: 'Menu sections, booking-ready layout, interactive JavaScript.',
    description: 'Modern, responsive restaurant website with menu sections, booking-ready content, and clean JavaScript interactions.',
    tags: ['HTML', 'CSS', 'JavaScript'],
    live: 'https://cafewebsite1.netlify.app',
    github: 'https://github.com/lukaSt605n/cafe-website',
  },
];


// ─── Card component ───────────────────────────────────────────────────────────
function ProjectCard({ project }) {
  const { title, images = [], alt, caption, description, featureNote, tags = [], live, github, buy } = project;
  const [idx, setIdx] = useState(0);
  const safeIdx = Math.max(0, Math.min(idx, images.length - 1));
  const hasThumbs = images.length > 1;

  return (
    <Card>
      {images[safeIdx] && (
        live ? (
          <a href={live} target="_blank" rel="noopener noreferrer">
            <MainImage src={images[safeIdx]} alt={alt || title} loading="lazy" />
          </a>
        ) : (
          <MainImage src={images[safeIdx]} alt={alt || title} loading="lazy" />
        )
      )}

      {hasThumbs && (
        <Thumbs aria-label={`${title} screenshots`}>
          {images.map((src, i) => (
            <ThumbButton
              key={src}
              type="button"
              onClick={() => setIdx(i)}
              active={i === safeIdx}
              aria-label={`Show screenshot ${i + 1} of ${images.length}`}
            >
              <img src={src} alt={`${title} thumbnail ${i + 1}`} loading="lazy" />
            </ThumbButton>
          ))}
        </Thumbs>
      )}

      {caption && <Caption>{caption}</Caption>}

      <h3>{title}</h3>
      <p>{description}</p>
      {featureNote && <FeatureNote>{featureNote}</FeatureNote>}

      {tags.length > 0 && (
        <Meta>
          {tags.map((t) => (
            <span key={t}>{t}</span>
          ))}
        </Meta>
      )}

      <ButtonGroup>
        {live && (
          <a href={live} target="_blank" rel="noopener noreferrer">
            Live
          </a>
        )}
        {github && (
          <a href={github} target="_blank" rel="noopener noreferrer">
            GitHub
          </a>
        )}
        {buy && (
          <a href={buy} target="_blank" rel="noopener noreferrer">
            Buy
          </a>
        )}
      </ButtonGroup>
    </Card>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
function Projects() {
  return (
    <ProjectsSection id="projects" aria-labelledby="projects-title">
      <Title id="projects-title">Projects</Title>
      <ProjectsGrid>
        {PROJECTS.map((p) => (
          <ProjectCard key={p.id} project={p} />
        ))}
      </ProjectsGrid>
    </ProjectsSection>
  );
}

export default Projects;
